import * as Yup from 'yup';
import SelectDropdown from '../../../../form-control/SelectDropdown';
import Button from '../../../../form-control/Button';
import { Formik } from 'formik';
import moment from 'moment';
import BrollyDatePicker from '../../../../dashboard/datepicker/datepicker';
import { ITableFilterItem } from '../../../../../resources/interfaces';
import Input from '../../../../form-control/Input';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFilterConfig, hasActiveFilters } from '../../../../../utils/filterUtils';
import Tooltip from '../../../../ToolTip';
import { FileUp, HelpCircle } from 'lucide-react';

export interface ITableFilter {
  searchPlaceholder?: string;
  onSearch?: (searchString: string) => void;
  getValues: (formData: any) => void;
  onSubmit?: () => void;
  filters?: ITableFilterItem[];
  onResetFilters?: () => void;
}

const TableFilterForm = ({ getValues, filters, onResetFilters }: ITableFilter) => {
  const [dirt, setDirty] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(filters ?? []);
  const filtersLength = tableFilters?.filter(f => f.value || (f.value?.from || f.value?.to)).length
  useEffect(() => {
    setTableFilters(filters ?? []);
  }, [filters]);

  const handleFormSubmit = async (values: any) => {
    getValues(tableFilters);
  };

  const handleFieldChange = (value: string, index: number, from?: Date, to?: Date, value2?: string) => {
    setDirty(true);
    const _tableFilters: ITableFilterItem[] = [...tableFilters]
    let thisFilter = { ...tableFilters[index] };

    if (from) {
      thisFilter = { ..._tableFilters[index], value: { ...thisFilter?.value, from: moment(from)?.format("YYYY-MM-DD") } }
    } else if (to) {
      thisFilter = { ..._tableFilters[index], value: thisFilter?.value ? { ...thisFilter?.value, to: moment(to)?.format("YYYY-MM-DD") } : { from: "", to: moment(to)?.format("YYYY-MM-DD") } }
    } else {
      thisFilter = { ..._tableFilters[index], value: value, value2: value2 };
    }

    _tableFilters[index] = thisFilter;
    setTableFilters([..._tableFilters]);
  };

  const handleSelectChange = (value: any, index: number) => {
    const filter = tableFilters[index];
    const config = getFilterConfig(filter.name);

    // Find the selected item from the list to get the display name
    const selectedItem = filter?.list?.find((item: any) => item.value === value);
    const displayValue = config.useValue2 ? selectedItem?.name : value;

    handleFieldChange(value, index, undefined, undefined, displayValue);
  };

  return (
    <div className="w-full bg-white dark:bg-darkBgrounded-lg shadow-sm dark:border dark:border-darkBg-border">
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filter by</h3>
          <Tooltip
            placement="bottom"
            bgColor="bg-black"
            textColor="text-white"
            containerClassName="rounded-full !w-[340px]"
            tooltipClassName="!w-[340px] text-xs"
            message={ 
                <p> Note that all filtered policies can be exported</p> 
            }>
            <HelpCircle className='h-3 w-3 cursor-pointer'/>
          </Tooltip>
        </div>

      </div>
      <div className="bg-white dark:bg-darkBg">
        <Formik
          initialValues={{
            dropDownOneListSelected: '',
            dropDownTwoListSelected: '',
            dropDownThreeListSelected: ''
          }}
          validationSchema={Yup.object().shape({})}
          onSubmit={async (values, { resetForm }) => {
            handleFormSubmit(values);
          }}>
          {({
            handleSubmit, handleChange, handleBlur, values, dirty, errors, touched, isSubmitting, setFieldValue, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className="p-5 w-full md:min-w-[350px] max-h-[400px] min-h-[200px] overflow-y-auto">
                <div className="w-full grid grid-cols-1 gap-4 my-4">
                  {tableFilters?.map((filter: ITableFilterItem, index: number) => (
                    // Keyed fragment: each .map() child needs a key, and only
                    // one of the conditional branches below actually renders
                    // for any given filter — putting the key on the inner
                    // element would be wrong because the renderer wouldn't
                    // know which branch fires.
                    <React.Fragment key={index}>
                      {filter?.type === "select" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <SelectDropdown
                            // label={filter?.label}
                            list={filter?.list}
                            listSelectedValue={filter?.value}
                            onChange={(value: any) => handleSelectChange(value, index)}
                            placeholder={filter?.placeholder ?? 'Select filter'}
                            wrapperClasses="w-full border-b-green-500"
                            selectInputClassName='relative w-full bg-white dark:bg-darkBg border-0 border-b border-gray-300 dark:border-darkBg-border shadow-sm pr-10 py-2 text-left cursor-pointer !ring-0 sm:text-sm rounded-none'
                            search={filter?.list?.length > 5 || filter?.showSearch}
                            disabled={filter?.disabled}
                          />
                        </div>
                      )}

                      {filter?.type === "multiSelect" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <SelectDropdown
                            // label={filter?.label}
                            list={filter?.list}
                            listSelectedValue={filter?.value}
                            onChange={(value: any) => handleSelectChange(value, index)}
                            placeholder={filter?.placeholder ?? 'Select options'}
                            wrapperClasses="w-full"
                            search={filter?.list?.length > 5 || filter?.showSearch}
                            disabled={filter?.disabled}
                          />
                          {filter?.multiple && (
                            <p className="text-xs text-gray-500 dark:text-darkBg-mainText mt-1">Hold Ctrl/Cmd to select multiple</p>
                          )}
                        </div>
                      )}

                      {filter?.type === "boolean" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <SelectDropdown
                            label={filter?.label}
                            list={[
                              { name: 'Yes', value: true },
                              { name: 'No', value: false }
                            ]}
                            listSelectedValue={filter?.value}
                            onChange={(value: any) => handleSelectChange(value, index)}
                            placeholder={filter?.placeholder ?? 'Select option'}
                            wrapperClasses="w-full"
                            disabled={filter?.disabled}
                          />
                        </div>
                      )}

                      {filter?.type === "numberRange" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <div className="flex items-center gap-2 w-full">
                            <div className="flex-1">
                              <Input
                                type="number"
                                placeholder="Min"
                                value={filter?.value?.min || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const newValue = { ...filter?.value, min: e.target.value };
                                  handleFieldChange(newValue, index);
                                }}
                                min={filter?.min}
                                max={filter?.max}
                                disabled={filter?.disabled}
                                className="w-full"
                              />
                            </div>
                            <span className="text-gray-500 dark:text-darkBg-mainText text-sm">to</span>
                            <div className="flex-1">
                              <Input
                                type="number"
                                placeholder="Max"
                                value={filter?.value?.max || ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const newValue = { ...filter?.value, max: e.target.value };
                                  handleFieldChange(newValue, index);
                                }}
                                min={filter?.min}
                                max={filter?.max}
                                disabled={filter?.disabled}
                                className="w-full"
                              />
                            </div>
                          </div>
                          {filter?.validation?.message && (
                            <p className="text-xs text-red-500 mt-1">{filter?.validation?.message}</p>
                          )}
                        </div>
                      )}

                      {filter?.type === "input" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <Input
                            type="text"
                            label={filter?.label}
                            placeholder={filter?.placeholder}
                            className="w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            value={`${filter?.value ?? ''}`}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              handleFieldChange(e?.target?.value, index);
                            }}
                            onBlur={handleBlur}
                            disabled={filter?.disabled}
                            required={filter?.validation?.required}
                            min={filter?.validation?.min}
                            max={filter?.validation?.max}
                          />
                          {filter?.validation?.message && (
                            <p className="text-xs text-red-500 mt-1">{filter?.validation?.message}</p>
                          )}
                        </div>
                      )}

                      {filter?.type === "dateRange" && (
                        <div className="space-y-2 flex items-end justify-center gap-5">
                          <label className="block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1 text-nowrap">
                            {filter?.label}
                            {filter?.validation?.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <div className="flex items-center gap-2 w-full">
                            <div className="flex-1">
                              <BrollyDatePicker
                                selectedDate={filter?.value?.from ? new Date(filter?.value?.from) : null}
                                onChange={(date: Date | null) => {
                                  if (date) {
                                    handleFieldChange("", index, date);
                                  }
                                }}
                                placeholderText="From"
                                className='relative w-full bg-white dark:bg-darkBg border-0 border-b border-gray-300 dark:border-darkBg-border shadow-sm pr-10 py-2 text-left cursor-pointer !ring-0 sm:text-sm rounded-none'
                              />
                            </div>
                            <div className="flex-1">
                              <BrollyDatePicker
                                selectedDate={filter?.value?.to ? new Date(filter?.value?.to) : null}
                                onChange={(date: Date | null) => {
                                  if (date) {
                                    handleFieldChange("", index, undefined, date);
                                  }
                                }}
                                placeholderText="To"
                                className='relative w-full bg-white dark:bg-darkBg border-0 border-b border-gray-300 dark:border-darkBg-border shadow-sm pr-10 py-2 text-left cursor-pointer !ring-0 sm:text-sm rounded-none'
                              />
                            </div>
                          </div>
                          {filter?.validation?.message && (
                            <p className="text-xs text-red-500 mt-1">{filter?.validation?.message}</p>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

              </div>

              <div className="flex gap-10 md:gap-0 flex-col md:flex-row items-center justify-between sticky bottom-0 bg-gray-50 dark:bg-gray-800 px-2 py-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between md:justify-normal items-center gap-3 w-full">
                  <Button
                    type="button"
                    label="Clear All"
                    onClick={() => {
                      setDirty(false);
                      onResetFilters?.();
                    }}
                    bgColor='secondary'
                  />
                  <div className="text-xs text-gray-500 dark:text-darkBg-mainText">
                    {hasActiveFilters(tableFilters || []) ? (
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-primary-500 rounded-full mr-2"></span>
                        {filtersLength} {filtersLength === 1 ? "filter" : "filters"} applied
                      </span>
                    ) : (
                      <span className="dark:text-darkBg-mainText">No filters applied</span>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  label={filtersLength > 1 ? "Apply Filters" : "Apply Filter"}
                  bgColor='primary'
                  // className={clsx('px-6 py-2 text-sm font-medium transition-all duration-200',)}
                  disabled={!dirty && !dirt}
                  curved
                  fullWidth
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default TableFilterForm;
